import React, { useMemo, useState, useEffect } from "react";
import {
  Button,
  FormControl,
  FormLabel,
  Input,
  Select,
  Switch,
  VStack,
  Textarea,
  HStack,
  Box,
  Image,
  Text,
  Spinner,
  Flex,
  Divider,
  Badge,
  SimpleGrid,
  IconButton,
} from "@chakra-ui/react";
import { AddIcon, MinusIcon } from "@chakra-ui/icons";
import { useJsApiLoader } from "@react-google-maps/api";
import UploadProgress from "../../../utils/UploadProgress";

import { uploadListingImagesToFirebase } from "./uploadListingImagesToFirebase";
import AutocompleteInput from "../../../LocationsModule/PlaceAutocompleteInput";
import { useCustomTheme } from "../../../contexts/CustomThemeContext";
import GlobalSpinner from "../../CustomReusables/GlobalSpinner";

interface AccommodationFormProps {
  onSubmit: (data: any) => void;
  initialData?: any;
}

const MAX_FILES = 12;

type RoomDraft = {
  id: string;
  title: string;
  pricePerNight: number;
  maxGuests?: number;
  bedrooms?: number;
  bathrooms?: number;
  beds?: number;
  availableRooms: number;

  images: string[];
  imageUrl: string;
  newFiles: File[];
};

const uid = () => Math.random().toString(36).slice(2, 10);
const cardsBorderRadii = "8px";

const AccommodationForm: React.FC<AccommodationFormProps> = ({
  onSubmit,
  initialData,
}) => {
  const { theme } = useCustomTheme();
  const safe = initialData || {};

  const API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "";
  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: API_KEY,
    libraries: ["places"],
  });

  useEffect(() => {
    const style = document.createElement("style");
    style.setAttribute("data-pac-zindex-fix", "true");
    style.innerHTML = `.pac-container { z-index: 20000 !important; }`;
    document.head.appendChild(style);
    return () => style.remove();
  }, []);

  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploading, setUploading] = useState(false);

  const [locationCoords, setLocationCoords] = useState<{
    lat: number;
    lng: number;
  } | null>(safe.locationCoords || null);

  const [formData, setFormData] = useState({
    name: safe.name || "",
    location: safe.location || "",
    pricePerNight: safe.pricePerNight ?? safe.price ?? 0,
    type: safe.type || safe.roomType || "Hotel",
    rooms: safe.rooms ?? 1,
    availability: safe.availability ?? true,

    description: safe.description || "",
    amenities: Array.isArray(safe.amenities)
      ? safe.amenities.join(", ")
      : safe.amenities || "",
    maxGuests: safe.maxGuests ?? 2,
    bedrooms: safe.bedrooms ?? 1,
    bathrooms: safe.bathrooms ?? 1,
    checkInFrom: safe.checkInFrom || "14:00",
    checkOutTo: safe.checkOutTo || "10:00",
    breakfastIncluded: safe.breakfastIncluded ?? false,
    freeCancellation: safe.freeCancellation ?? true,
  });

  const [existingImages, setExistingImages] = useState<string[]>(
    Array.isArray(safe.images) && safe.images.length
      ? safe.images
      : safe.imageUrl
        ? [safe.imageUrl]
        : [],
  );
  const [files, setFiles] = useState<File[]>([]);

  const [roomOptions, setRoomOptions] = useState<RoomDraft[]>(() => {
    if (Array.isArray(safe.roomOptions) && safe.roomOptions.length) {
      return safe.roomOptions.map((r: any) => ({
        id: String(r.id || uid()),
        title: String(r.title || ""),
        pricePerNight: Number(r.pricePerNight || 0),
        maxGuests: r.maxGuests != null ? Number(r.maxGuests) : undefined,
        bedrooms: r.bedrooms != null ? Number(r.bedrooms) : undefined,
        bathrooms: r.bathrooms != null ? Number(r.bathrooms) : undefined,
        beds: r.beds != null ? Number(r.beds) : undefined,
        availableRooms: r.availableRooms != null ? Number(r.availableRooms) : 1,
        images: Array.isArray(r.images) ? r.images : [],
        imageUrl: String(
          r.imageUrl || (Array.isArray(r.images) && r.images[0]) || "",
        ),
        newFiles: [],
      }));
    }
    return [];
  });

  const [hasRoomOptions, setHasRoomOptions] = useState<boolean>(
    Array.isArray(safe.roomOptions) && safe.roomOptions.length > 0,
  );

  useEffect(() => {
    if (roomOptions.length > 0) setHasRoomOptions(true);
  }, [roomOptions.length]);

  useEffect(() => {
    const s = initialData || {};

    setFormData({
      name: s.name || "",
      location: s.location || "",
      pricePerNight: s.pricePerNight ?? s.price ?? 0,
      type: s.type || s.roomType || "Hotel",
      rooms: s.rooms ?? 1,
      availability: s.availability ?? true,

      description: s.description || "",
      amenities: Array.isArray(s.amenities)
        ? s.amenities.join(", ")
        : s.amenities || "",
      maxGuests: s.maxGuests ?? 2,
      bedrooms: s.bedrooms ?? 1,
      bathrooms: s.bathrooms ?? 1,
      checkInFrom: s.checkInFrom || "14:00",
      checkOutTo: s.checkOutTo || "10:00",
      breakfastIncluded: s.breakfastIncluded ?? false,
      freeCancellation: s.freeCancellation ?? true,
    });

    setExistingImages(
      Array.isArray(s.images) && s.images.length
        ? s.images
        : s.imageUrl
          ? [s.imageUrl]
          : [],
    );
    setLocationCoords(s.locationCoords || null);
    setFiles([]);
    setUploading(false);
    setUploadProgress(0);

    const incomingRooms = Array.isArray(s.roomOptions) ? s.roomOptions : [];
    setHasRoomOptions(incomingRooms.length > 0);

    setRoomOptions(() => {
      if (incomingRooms.length) {
        return incomingRooms.map((r: any) => ({
          id: String(r.id || uid()),
          title: String(r.title || ""),
          pricePerNight: Number(r.pricePerNight || 0),
          maxGuests: r.maxGuests != null ? Number(r.maxGuests) : undefined,
          bedrooms: r.bedrooms != null ? Number(r.bedrooms) : undefined,
          bathrooms: r.bathrooms != null ? Number(r.bathrooms) : undefined,
          beds: r.beds != null ? Number(r.beds) : undefined,
          availableRooms:
            r.availableRooms != null ? Number(r.availableRooms) : 1,
          images: Array.isArray(r.images) ? r.images : [],
          imageUrl: String(
            r.imageUrl || (Array.isArray(r.images) && r.images[0]) || "",
          ),
          newFiles: [],
        }));
      }
      return [];
    });
  }, [initialData]);

  const newPreviewUrls = useMemo(
    () => files.map((f) => URL.createObjectURL(f)),
    [files],
  );
  const previewList = useMemo(
    () => [...existingImages, ...newPreviewUrls],
    [existingImages, newPreviewUrls],
  );

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;

    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handlePickFiles = (e: React.ChangeEvent<HTMLInputElement>) => {
    const picked = Array.from(e.target.files || []);
    if (!picked.length) return;

    setFiles((prev) => [...prev, ...picked].slice(0, MAX_FILES));
    e.target.value = "";
  };

  const removeAt = (index: number) => {
    if (index < existingImages.length) {
      setExistingImages((prev) => prev.filter((_, i) => i !== index));
      return;
    }
    const localIdx = index - existingImages.length;
    setFiles((prev) => prev.filter((_, i) => i !== localIdx));
  };

  const addRoomOption = () => {
    setHasRoomOptions(true);
    setRoomOptions((prev) => [
      ...prev,
      {
        id: uid(),
        title: "Standard Room",
        pricePerNight: 0,
        maxGuests: 2,
        bedrooms: 1,
        bathrooms: 1,
        beds: 1,
        availableRooms: 1,
        images: [],
        imageUrl: "",
        newFiles: [],
      },
    ]);
  };

  const updateRoom = (roomId: string, patch: Partial<RoomDraft>) => {
    setRoomOptions((prev) =>
      prev.map((r) => (r.id === roomId ? { ...r, ...patch } : r)),
    );
  };

  const removeRoom = (roomId: string) => {
    setRoomOptions((prev) => prev.filter((r) => r.id !== roomId));
  };

  const incRooms = (roomId: string) => {
    setRoomOptions((prev) =>
      prev.map((r) =>
        r.id === roomId
          ? { ...r, availableRooms: Number(r.availableRooms || 0) + 1 }
          : r,
      ),
    );
  };

  const decRooms = (roomId: string) => {
    setRoomOptions((prev) =>
      prev.map((r) =>
        r.id === roomId
          ? {
              ...r,
              availableRooms: Math.max(0, Number(r.availableRooms || 0) - 1),
            }
          : r,
      ),
    );
  };

  const incRoomGuests = (roomId: string) => {
    setRoomOptions((prev) =>
      prev.map((r) =>
        r.id === roomId ? { ...r, maxGuests: Number(r.maxGuests || 1) + 1 } : r,
      ),
    );
  };

  const decRoomGuests = (roomId: string) => {
    setRoomOptions((prev) =>
      prev.map((r) =>
        r.id === roomId
          ? { ...r, maxGuests: Math.max(1, Number(r.maxGuests || 1) - 1) }
          : r,
      ),
    );
  };

  const pickRoomFiles = (
    roomId: string,
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const picked = Array.from(e.target.files || []);
    if (!picked.length) return;

    setRoomOptions((prev) =>
      prev.map((r) =>
        r.id === roomId
          ? {
              ...r,
              newFiles: [...(r.newFiles || []), ...picked].slice(0, MAX_FILES),
            }
          : r,
      ),
    );

    e.target.value = "";
  };

  const removeRoomImageAt = (roomId: string, index: number) => {
    setRoomOptions((prev) =>
      prev.map((r) => {
        if (r.id !== roomId) return r;

        const existingLen = r.images.length;

        if (index < existingLen) {
          const removedUrl = r.images[index];
          const newImages = r.images.filter((_, i) => i !== index);
          const newCover =
            r.imageUrl && r.imageUrl === removedUrl
              ? newImages[0] || ""
              : r.imageUrl;
          return { ...r, images: newImages, imageUrl: newCover };
        }

        const localIdx = index - existingLen;
        const newFiles = r.newFiles.filter((_, i) => i !== localIdx);
        return { ...r, newFiles };
      }),
    );
  };

  const roomPreviewUrls = (room: RoomDraft) => {
    const locals = room.newFiles.map((f) => URL.createObjectURL(f));
    const cover = room.imageUrl ? [room.imageUrl] : [];
    const merged = [...cover, ...(room.images || []), ...locals].filter(
      Boolean,
    );
    return Array.from(new Set(merged));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const userId = localStorage.getItem("userId") || "guest";
    const key = safe._id || `${userId}-${Date.now()}`;

    try {
      setUploading(true);
      setUploadProgress(10);

      let imageUrls: string[] = existingImages || [];
      if (files.length > 0) {
        setUploadProgress(25);
        const uploaded = await uploadListingImagesToFirebase(
          files,
          key,
          "accommodations",
        );
        imageUrls = [...imageUrls, ...uploaded];
      }

      let uploadedRoomOptions: any[] = [];

      if (hasRoomOptions && roomOptions.length > 0) {
        setUploadProgress(45);

        const results = await Promise.all(
          roomOptions.map(async (room) => {
            let roomImages = Array.isArray(room.images) ? room.images : [];

            if (room.newFiles?.length) {
              const uploaded = await uploadListingImagesToFirebase(
                room.newFiles,
                `${key}-${room.id}`,
                "accommodation_rooms",
              );
              roomImages = [...roomImages, ...uploaded];
            }

            const cover = room.imageUrl || roomImages[0] || "";

            return {
              id: room.id,
              title: String(room.title || "").trim(),
              pricePerNight:
                Number(room.pricePerNight || 0) > 0
                  ? Number(room.pricePerNight)
                  : Number(formData.pricePerNight || 0),
              maxGuests:
                room.maxGuests != null ? Number(room.maxGuests) : undefined,
              bedrooms:
                room.bedrooms != null ? Number(room.bedrooms) : undefined,
              bathrooms:
                room.bathrooms != null ? Number(room.bathrooms) : undefined,
              beds: room.beds != null ? Number(room.beds) : undefined,
              availableRooms:
                room.availableRooms != null ? Number(room.availableRooms) : 1,
              images: roomImages,
              imageUrl: cover,
            };
          }),
        );

        uploadedRoomOptions = results.filter((r) => r.title.length > 0);
      }

      setUploadProgress(85);

      const amenitiesArray = String(formData.amenities || "")
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);

      const finalImages = Array.isArray(imageUrls) ? imageUrls : [];

      const payload = {
        ...formData,
        userId,

        pricePerNight: Number(formData.pricePerNight),
        rooms: Number(formData.rooms),
        maxGuests: Number(formData.maxGuests),
        bedrooms: Number(formData.bedrooms),
        bathrooms: Number(formData.bathrooms),

        amenities: amenitiesArray,

        images: finalImages,
        imageUrl: finalImages?.[0] || safe.imageUrl || "",

        locationCoords: locationCoords || undefined,

        roomOptions: hasRoomOptions ? uploadedRoomOptions : undefined,
      };

      console.log("ACCOMMODATION SUBMIT PAYLOAD:", payload);

      onSubmit(payload);

      setUploadProgress(100);
    } finally {
      setUploading(false);
      setTimeout(() => setUploadProgress(0), 250);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <VStack spacing={4} align="stretch" pb={2}>
        <FormControl isRequired>
          <FormLabel color={theme.text}>Name</FormLabel>
          <Input
            name="name"
            value={formData.name}
            onChange={handleChange}
            bg={theme.inputBg}
            color={theme.text}
            borderColor={theme.inputBorder}
            _placeholder={{ color: theme.inputPlaceholder }}
            _hover={{ borderColor: theme.inputBorderFocus }}
            _focus={{
              borderColor: theme.inputBorderFocus,
              boxShadow: `0 0 0 1px ${theme.inputBorderFocus}`,
            }}
          />
        </FormControl>

        <FormControl isRequired>
          <FormLabel color={theme.text}>Location</FormLabel>

          {!API_KEY ? (
            <Input
              name="location"
              placeholder="Enter location"
              value={formData.location}
              onChange={handleChange}
              bg={theme.inputBg}
              color={theme.text}
              borderColor={theme.inputBorder}
              _placeholder={{ color: theme.inputPlaceholder }}
              _hover={{ borderColor: theme.inputBorderFocus }}
              _focus={{
                borderColor: theme.inputBorderFocus,
                boxShadow: `0 0 0 1px ${theme.inputBorderFocus}`,
              }}
            />
          ) : loadError ? (
            <Box>
              <Text fontSize="sm" color="red.500" mb={2}>
                Maps failed to load. Using manual input.
              </Text>
              <Input
                name="location"
                placeholder="Enter location"
                value={formData.location}
                onChange={handleChange}
                bg={theme.inputBg}
                color={theme.text}
                borderColor={theme.inputBorder}
                _placeholder={{ color: theme.inputPlaceholder }}
                _hover={{ borderColor: theme.inputBorderFocus }}
                _focus={{
                  borderColor: theme.inputBorderFocus,
                  boxShadow: `0 0 0 1px ${theme.inputBorderFocus}`,
                }}
              />
            </Box>
          ) : !isLoaded ? (
            <Flex align="center" gap={2}>
              <GlobalSpinner />
              <Text fontSize="sm" color={theme.textSubtle}>
                Loading location search…
              </Text>
            </Flex>
          ) : (
            <AutocompleteInput
              placeholder="Search location..."
              value={formData.location}
              onPlaceSelected={(address) =>
                setFormData((p) => ({ ...p, location: address }))
              }
              onLocationSelected={(coords) => setLocationCoords(coords)}
            />
          )}

          {locationCoords && (
            <Text fontSize="xs" color={theme.textSubtle} mt={1}>
              Lat: {locationCoords.lat.toFixed(5)} • Lng:{" "}
              {locationCoords.lng.toFixed(5)}
            </Text>
          )}
        </FormControl>

        <HStack spacing={3}>
          <FormControl isRequired>
            <FormLabel color={theme.text}>Type</FormLabel>
            <Select
              name="type"
              value={formData.type}
              onChange={handleChange}
              bg={theme.inputBg}
              color={theme.text}
              borderColor={theme.inputBorder}
              _hover={{ borderColor: theme.inputBorderFocus }}
              _focus={{
                borderColor: theme.inputBorderFocus,
                boxShadow: `0 0 0 1px ${theme.inputBorderFocus}`,
              }}
            >
              <option value="Hotel">Hotel</option>
              <option value="Apartment">Apartment</option>
              <option value="Guest House">Guest House</option>
              <option value="Lodge">Lodge</option>
              <option value="Villa">Villa</option>
            </Select>
          </FormControl>

          <FormControl isRequired>
            <FormLabel color={theme.text}>Rooms (legacy)</FormLabel>
            <Input
              type="number"
              name="rooms"
              value={formData.rooms as any}
              onChange={handleChange}
              bg={theme.inputBg}
              color={theme.text}
              borderColor={theme.inputBorder}
              _hover={{ borderColor: theme.inputBorderFocus }}
              _focus={{
                borderColor: theme.inputBorderFocus,
                boxShadow: `0 0 0 1px ${theme.inputBorderFocus}`,
              }}
            />
          </FormControl>
        </HStack>

        <FormControl isRequired>
          <FormLabel color={theme.text}>Price per night (legacy)</FormLabel>
          <Input
            type="number"
            name="pricePerNight"
            value={formData.pricePerNight as any}
            onChange={handleChange}
            bg={theme.inputBg}
            color={theme.text}
            borderColor={theme.inputBorder}
            _hover={{ borderColor: theme.inputBorderFocus }}
            _focus={{
              borderColor: theme.inputBorderFocus,
              boxShadow: `0 0 0 1px ${theme.inputBorderFocus}`,
            }}
          />
          <Text fontSize="xs" color={theme.textSubtle} mt={1}>
            Room price can be left empty — it will fallback to this listing
            price.
          </Text>
        </FormControl>

        <FormControl>
          <FormLabel color={theme.text}>Max guests (listing)</FormLabel>
          <HStack
            spacing={0}
            border="1px solid"
            borderColor={theme.inputBorder}
            borderRadius={cardsBorderRadii}
            overflow="hidden"
            bg={theme.inputBg}
            maxW="220px"
          >
            <Button
              borderRadius="0"
              variant="ghost"
              minW="44px"
              onClick={() =>
                setFormData((prev) => ({
                  ...prev,
                  maxGuests: Math.max(1, Number(prev.maxGuests || 1) - 1),
                }))
              }
              color={theme.text}
              _hover={{ bg: theme.hover }}
            >
              -
            </Button>

            <Box
              flex="1"
              textAlign="center"
              color={theme.text}
              fontWeight="600"
              py={2}
              borderLeft="1px solid"
              borderRight="1px solid"
              borderColor={theme.inputBorder}
            >
              {Number(formData.maxGuests || 1)}
            </Box>

            <Button
              borderRadius="0"
              variant="ghost"
              minW="44px"
              onClick={() =>
                setFormData((prev) => ({
                  ...prev,
                  maxGuests: Number(prev.maxGuests || 1) + 1,
                }))
              }
              color={theme.text}
              _hover={{ bg: theme.hover }}
            >
              +
            </Button>
          </HStack>
        </FormControl>

        <FormControl>
          <FormLabel color={theme.text}>Description</FormLabel>
          <Textarea
            name="description"
            value={formData.description}
            onChange={handleChange}
            bg={theme.inputBg}
            color={theme.text}
            borderColor={theme.inputBorder}
            _placeholder={{ color: theme.inputPlaceholder }}
            _hover={{ borderColor: theme.inputBorderFocus }}
            _focus={{
              borderColor: theme.inputBorderFocus,
              boxShadow: `0 0 0 1px ${theme.inputBorderFocus}`,
            }}
          />
        </FormControl>

        <FormControl>
          <FormLabel color={theme.text}>Amenities (comma separated)</FormLabel>
          <Input
            name="amenities"
            value={formData.amenities as any}
            onChange={handleChange}
            placeholder="WiFi, Parking, Pool..."
            bg={theme.inputBg}
            color={theme.text}
            borderColor={theme.inputBorder}
            _placeholder={{ color: theme.inputPlaceholder }}
            _hover={{ borderColor: theme.inputBorderFocus }}
            _focus={{
              borderColor: theme.inputBorderFocus,
              boxShadow: `0 0 0 1px ${theme.inputBorderFocus}`,
            }}
          />
        </FormControl>

        <HStack spacing={6} justify="space-between" flexWrap="wrap">
          <FormControl display="flex" alignItems="center">
            <FormLabel htmlFor="availability" mb="0" color={theme.text}>
              Available
            </FormLabel>
            <Switch
              id="availability"
              name="availability"
              isChecked={!!formData.availability}
              onChange={handleChange}
            />
          </FormControl>

          <FormControl display="flex" alignItems="center">
            <FormLabel htmlFor="breakfastIncluded" mb="0" color={theme.text}>
              Breakfast included
            </FormLabel>
            <Switch
              id="breakfastIncluded"
              name="breakfastIncluded"
              isChecked={!!formData.breakfastIncluded}
              onChange={handleChange}
            />
          </FormControl>

          <FormControl display="flex" alignItems="center">
            <FormLabel htmlFor="freeCancellation" mb="0" color={theme.text}>
              Free cancellation
            </FormLabel>
            <Switch
              id="freeCancellation"
              name="freeCancellation"
              isChecked={!!formData.freeCancellation}
              onChange={handleChange}
            />
          </FormControl>
        </HStack>

        <Divider borderColor={theme.divider} />

        <FormControl
          display="flex"
          alignItems="center"
          justifyContent="space-between"
        >
          <Box>
            <Text fontWeight="bold" color={theme.text}>
              Has multiple room types?
            </Text>
            <Text fontSize="sm" color={theme.textSubtle}>
              This stays ON if you have any rooms added.
            </Text>
          </Box>
          <Switch
            isChecked={hasRoomOptions}
            onChange={(e) => {
              if (roomOptions.length > 0) {
                setHasRoomOptions(true);
                return;
              }
              setHasRoomOptions(e.target.checked);
            }}
          />
        </FormControl>

        {hasRoomOptions && (
          <Box
            borderWidth="1px"
            borderColor={theme.border}
            borderRadius={cardsBorderRadii}
            p={3}
            bg={theme.surfaceAlt}
            boxShadow={theme.shadowSm}
          >
            <HStack justify="space-between" mb={3}>
              <Text fontWeight="bold" color={theme.text}>
                Room options
              </Text>
              <Button
                size="sm"
                onClick={addRoomOption}
                color={theme.textOnPrimary}
                bgImage={theme.gradients.primaryButton}
                _hover={{ bgImage: theme.gradients.primaryButtonHover }}
                boxShadow={theme.shadowSm}
              >
                Add room
              </Button>
            </HStack>

            <VStack align="stretch" spacing={3}>
              {roomOptions.map((room) => (
                <Box
                  key={room.id}
                  borderWidth="1px"
                  borderColor={theme.border}
                  borderRadius={cardsBorderRadii}
                  p={3}
                  bg={theme.surface}
                  boxShadow={theme.shadowSm}
                >
                  <HStack justify="space-between" mb={2}>
                    <Badge colorScheme="purple">{room.title || "Room"}</Badge>
                    <Button
                      size="xs"
                      colorScheme="red"
                      variant="outline"
                      onClick={() => removeRoom(room.id)}
                    >
                      Remove
                    </Button>
                  </HStack>

                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
                    <FormControl>
                      <FormLabel color={theme.text}>Title</FormLabel>
                      <Input
                        value={room.title}
                        onChange={(e) =>
                          updateRoom(room.id, { title: e.target.value })
                        }
                        placeholder="Standard Room"
                        bg={theme.inputBg}
                        color={theme.text}
                        borderColor={theme.inputBorder}
                        _placeholder={{ color: theme.inputPlaceholder }}
                        _hover={{ borderColor: theme.inputBorderFocus }}
                        _focus={{
                          borderColor: theme.inputBorderFocus,
                          boxShadow: `0 0 0 1px ${theme.inputBorderFocus}`,
                        }}
                      />
                    </FormControl>

                    <FormControl>
                      <FormLabel color={theme.text}>Price / night</FormLabel>
                      <Input
                        type="number"
                        value={room.pricePerNight as any}
                        onChange={(e) =>
                          updateRoom(room.id, {
                            pricePerNight: Number(e.target.value),
                          })
                        }
                        bg={theme.inputBg}
                        color={theme.text}
                        borderColor={theme.inputBorder}
                        _hover={{ borderColor: theme.inputBorderFocus }}
                        _focus={{
                          borderColor: theme.inputBorderFocus,
                          boxShadow: `0 0 0 1px ${theme.inputBorderFocus}`,
                        }}
                      />
                      <Text fontSize="xs" color={theme.textSubtle} mt={1}>
                        Leave blank/0 to fallback to listing price.
                      </Text>
                    </FormControl>

                    <FormControl>
                      <FormLabel color={theme.text}>Available rooms</FormLabel>
                      <HStack>
                        <IconButton
                          aria-label="Decrease"
                          icon={<MinusIcon />}
                          size="sm"
                          onClick={() => decRooms(room.id)}
                          variant="outline"
                          borderColor={theme.borderStrong}
                          color={theme.text}
                          _hover={{ bg: theme.hover }}
                        />
                        <Input
                          type="number"
                          value={room.availableRooms as any}
                          onChange={(e) =>
                            updateRoom(room.id, {
                              availableRooms: Number(e.target.value),
                            })
                          }
                          bg={theme.inputBg}
                          color={theme.text}
                          borderColor={theme.inputBorder}
                          _hover={{ borderColor: theme.inputBorderFocus }}
                          _focus={{
                            borderColor: theme.inputBorderFocus,
                            boxShadow: `0 0 0 1px ${theme.inputBorderFocus}`,
                          }}
                        />
                        <IconButton
                          aria-label="Increase"
                          icon={<AddIcon />}
                          size="sm"
                          onClick={() => incRooms(room.id)}
                          variant="outline"
                          borderColor={theme.borderStrong}
                          color={theme.text}
                          _hover={{ bg: theme.hover }}
                        />
                      </HStack>
                    </FormControl>

                    <FormControl>
                      <FormLabel color={theme.text}>Max guests</FormLabel>
                      <HStack
                        spacing={0}
                        border="1px solid"
                        borderColor={theme.inputBorder}
                        borderRadius={cardsBorderRadii}
                        overflow="hidden"
                        bg={theme.inputBg}
                      >
                        <Button
                          borderRadius="0"
                          variant="ghost"
                          minW="44px"
                          onClick={() => decRoomGuests(room.id)}
                          color={theme.text}
                          _hover={{ bg: theme.hover }}
                        >
                          -
                        </Button>

                        <Box
                          flex="1"
                          textAlign="center"
                          color={theme.text}
                          fontWeight="600"
                          py={2}
                          borderLeft="1px solid"
                          borderRight="1px solid"
                          borderColor={theme.inputBorder}
                        >
                          {Number(room.maxGuests || 1)}
                        </Box>

                        <Button
                          borderRadius="0"
                          variant="ghost"
                          minW="44px"
                          onClick={() => incRoomGuests(room.id)}
                          color={theme.text}
                          _hover={{ bg: theme.hover }}
                        >
                          +
                        </Button>
                      </HStack>
                    </FormControl>

                    <FormControl>
                      <FormLabel color={theme.text}>Bedrooms</FormLabel>
                      <Input
                        type="number"
                        value={room.bedrooms as any}
                        onChange={(e) =>
                          updateRoom(room.id, {
                            bedrooms: Number(e.target.value),
                          })
                        }
                        bg={theme.inputBg}
                        color={theme.text}
                        borderColor={theme.inputBorder}
                        _hover={{ borderColor: theme.inputBorderFocus }}
                        _focus={{
                          borderColor: theme.inputBorderFocus,
                          boxShadow: `0 0 0 1px ${theme.inputBorderFocus}`,
                        }}
                      />
                    </FormControl>

                    <FormControl>
                      <FormLabel color={theme.text}>Bathrooms</FormLabel>
                      <Input
                        type="number"
                        value={room.bathrooms as any}
                        onChange={(e) =>
                          updateRoom(room.id, {
                            bathrooms: Number(e.target.value),
                          })
                        }
                        bg={theme.inputBg}
                        color={theme.text}
                        borderColor={theme.inputBorder}
                        _hover={{ borderColor: theme.inputBorderFocus }}
                        _focus={{
                          borderColor: theme.inputBorderFocus,
                          boxShadow: `0 0 0 1px ${theme.inputBorderFocus}`,
                        }}
                      />
                    </FormControl>

                    <FormControl>
                      <FormLabel color={theme.text}>Beds</FormLabel>
                      <Input
                        type="number"
                        value={room.beds as any}
                        onChange={(e) =>
                          updateRoom(room.id, {
                            beds: Number(e.target.value),
                          })
                        }
                        bg={theme.inputBg}
                        color={theme.text}
                        borderColor={theme.inputBorder}
                        _hover={{ borderColor: theme.inputBorderFocus }}
                        _focus={{
                          borderColor: theme.inputBorderFocus,
                          boxShadow: `0 0 0 1px ${theme.inputBorderFocus}`,
                        }}
                      />
                    </FormControl>
                  </SimpleGrid>

                  <Divider my={3} borderColor={theme.divider} />

                  <FormControl>
                    <FormLabel color={theme.text}>Room images</FormLabel>
                    <Input
                      type="file"
                      multiple
                      accept="image/*"
                      onChange={(e) => pickRoomFiles(room.id, e)}
                      bg={theme.inputBg}
                      color={theme.text}
                      borderColor={theme.inputBorder}
                      _hover={{ borderColor: theme.inputBorderFocus }}
                      _focus={{
                        borderColor: theme.inputBorderFocus,
                        boxShadow: `0 0 0 1px ${theme.inputBorderFocus}`,
                      }}
                    />
                    <Text fontSize="xs" color={theme.textSubtle} mt={1}>
                      Images uploaded here belong to this room type only.
                    </Text>
                  </FormControl>

                  {roomPreviewUrls(room).length > 0 && (
                    <Box mt={3}>
                      <Text
                        fontSize="sm"
                        fontWeight="bold"
                        mb={2}
                        color={theme.text}
                      >
                        Room Images ({roomPreviewUrls(room).length})
                      </Text>

                      <HStack spacing={2} flexWrap="wrap">
                        {roomPreviewUrls(room).map((url, idx) => (
                          <Box key={`${url}-${idx}`} position="relative">
                            <Image
                              src={url}
                              w="70px"
                              h="50px"
                              objectFit="cover"
                              borderRadius={cardsBorderRadii}
                              border="1px solid"
                              borderColor={theme.border}
                            />
                            <Button
                              type="button"
                              size="xs"
                              colorScheme="red"
                              position="absolute"
                              top="-8px"
                              right="-8px"
                              borderRadius={cardsBorderRadii}
                              onClick={() => removeRoomImageAt(room.id, idx)}
                            >
                              ×
                            </Button>
                          </Box>
                        ))}
                      </HStack>
                    </Box>
                  )}
                </Box>
              ))}

              {roomOptions.length === 0 && (
                <Text color={theme.textSubtle} fontSize="sm">
                  Add at least one room option.
                </Text>
              )}
            </VStack>
          </Box>
        )}

        <Divider borderColor={theme.divider} />

        <UploadProgress
          isUploading={uploading}
          progress={uploadProgress}
          label="Uploading..."
        />

        <FormControl>
          <FormLabel color={theme.text}>Property Images (multiple)</FormLabel>
          <Input
            type="file"
            multiple
            accept="image/*"
            onChange={handlePickFiles}
            bg={theme.inputBg}
            color={theme.text}
            borderColor={theme.inputBorder}
            _hover={{ borderColor: theme.inputBorderFocus }}
            _focus={{
              borderColor: theme.inputBorderFocus,
              boxShadow: `0 0 0 1px ${theme.inputBorderFocus}`,
            }}
          />
          <Text fontSize="xs" color={theme.textSubtle} mt={1}>
            These are the main listing images (property-level).
          </Text>
        </FormControl>

        {previewList.length > 0 && (
          <Box>
            <Text fontSize="sm" fontWeight="bold" mb={2} color={theme.text}>
              Property Images ({previewList.length})
            </Text>
            <HStack spacing={2} flexWrap="wrap">
              {previewList.map((url, idx) => (
                <Box key={`${url}-${idx}`} position="relative">
                  <Image
                    src={url}
                    w="70px"
                    h="50px"
                    objectFit="cover"
                    borderRadius={cardsBorderRadii}
                    border="1px solid"
                    borderColor={theme.border}
                  />
                  <Button
                    type="button"
                    size="xs"
                    colorScheme="red"
                    position="absolute"
                    top="-8px"
                    right="-8px"
                    borderRadius={cardsBorderRadii}
                    onClick={() => removeAt(idx)}
                  >
                    ×
                  </Button>
                </Box>
              ))}
            </HStack>
          </Box>
        )}

        <Button
          type="submit"
          color={theme.textOnPrimary}
          bgImage={theme.gradients.primaryButton}
          _hover={{ bgImage: theme.gradients.primaryButtonHover }}
          boxShadow={theme.shadowSm}
          isDisabled={uploading}
        >
          {uploading ? (
            <HStack>
              <GlobalSpinner />
              <Text>Uploading...</Text>
            </HStack>
          ) : initialData ? (
            "Update"
          ) : (
            "Submit"
          )}
        </Button>
      </VStack>
    </form>
  );
};

export default AccommodationForm;
